import {Bounce, Power1, Power2, Power3, Back, gsap} from 'gsap';
import {StageLayer} from '@nolimitcity/slot-launcher/bin/StageLayer';
import {SlotGame} from '@nolimitcity/slot-game/bin/SlotGame';
import {IDimOrientation, LayoutDimData, ResizeHelper} from '../../../core/utils/ResizeHelper';
import {BitmapTextStyleOptions, TextStyleOptions} from '../../../core/setting/text/IText';
import {BitmapCountUp} from '../../../core/component/BitmapCountUp';
import {Translator} from '../../../core/setting/Translator';
import {TranslationTexts} from '../../config/translate/TranslationTexts';
import {Helper} from '../../../core/utils/Helper';
import {MathHelper} from '../../../core/utils/MathHelper';
import {AnimationEffects} from '../../../core/effects/AnimationEffects';
import {GameAssets} from '../../GameAssets';

export class BigWinCountDisplay extends PIXI.Container implements StageLayer {
    protected readonly counterDim: IDimOrientation = {
        landscape: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 360),
        },
        portrait: {
            scale: new PIXI.Point(0.7, 0.7),
            position: new PIXI.Point(360, 360),
        },
    };
    private readonly _style_low: BitmapTextStyleOptions = {
        fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP_LOW,
        fontSize: 68 / 2,
        letterSpacing: 12,
    };
    private readonly _styleWays: TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 7.5,
        fill: 'white',
        fontFamily: 'Helvetica',
        fontSize: 30,
        fontStyle: 'italic',
        fontWeight: 'bold',
        miterLimit: 0,
        strokeThickness: 3,
    };
    private downDistance: number = 220;
    private waysYOffset: number = 120;
    // private win_FX: Sequence;
    protected _countUp: BitmapCountUp;
    protected _waysTxt: PIXI.Text;
    protected _timeline: gsap.core.Timeline;
    protected _bounceTl: gsap.core.Timeline;
    protected _movementTL: gsap.core.Timeline;
    protected _waysTL: gsap.core.Timeline;


    public get dim(): LayoutDimData {
        return ResizeHelper.calcDim(this.counterDim);
    }

    public get timeRemaining(): number {
        return this._timeline && this._timeline.isActive() ? this._timeline.duration() - this._timeline.totalTime() : 0;
    }

    private set waysText(value: number) {
        this._waysTxt.text =
            value.toString() + ' ' + Translator.translate(value == 1 ? TranslationTexts.WAY_WON : TranslationTexts.WAYS_WON);
        this._waysTxt.updateTransform();
    }

    constructor() {
        super();
        this.init();
    }

    protected init(): void {
        this._countUp = this.addChild(new BitmapCountUp(this._style_low));
        this._countUp.alpha = 0;
        this._countUp.anchor = new PIXI.Point(0.5, 0.5);
        this._countUp.position.set(0, -100);

        this._waysTxt = this.addChild(new PIXI.Text(''));
        this._waysTxt.alpha = 0;
        this._waysTxt.style = this._styleWays;
        this._waysTxt.anchor.set(0.5, 0.5);
        this._waysTxt.position.set(0, 20);
        this.onResize();
    }

    public playBigWin(from: number, to: number, duration: number, ways: number = 1): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(
            this._timeline!,
            new gsap.core.Timeline({onComplete: () => this.countUpAnimVal(to)})
        );

        const deciNumber: number = MathHelper.floorToDecimals(to, 2);
        this._timeline.add(
            this._countUp.getCountUpAnimation(from, deciNumber, {duration: duration, ease: Power1.easeOut}),
            0
        );

        if (from == 0) {
            this._timeline.fromTo(this._countUp, duration * 0.8, {alpha: 0}, {
                duration: duration * 0.8,
                alpha: 1,
                ease: 'power1.easeOut'
            }, 0);
            if (duration) {
                this._timeline.fromTo(
                    this._countUp.scale,
                    duration - 1,
                    {x: 0.78, y: 0.78},
                    {x: 1, y: 1, duration: duration - 1, ease: 'power1.easeOut'},
                    0
                );
            }
        } else {
            this._countUp.alpha = 1;
        }

        const pos = this._countUp.position;
        this._timeline.add(
            AnimationEffects.Shake({
                x: 1.5,
                y: 1.5,
                obj: this._countUp,
                hertz: 80,
                duration: 0.5,
                updateOnComplete: true,
                positionCb: () => pos, //new PIXI.Point(0,300),
                Intensity: 2.75,
                ease: Power2.easeOut,
            })
        );

        this._timeline.to(this._countUp.scale, 1, {x: 0.78, y: 0.78, duration: 1, ease: 'power3.easeIn'}, '-=1');

        this.waysText = ways;

        return this._timeline;
    }

    private countUpAnimVal(val: number): void {
        this._countUp.showValue(val);
    }

    public setValue(val: number, ways: number): void {
        this._countUp.showValue(val);
        this._countUp.alpha = this._waysTxt.alpha = 1;
        this.waysText = ways;
    }


    public showWays(duration: number = 0.65, resetPos?: boolean): void {
        this._waysTL = Helper.setTimeline(this._waysTL, new gsap.core.Timeline(), true);

        if (resetPos) this._waysTL.to(this._countUp, duration / 2, {y: -100, ease: 'back.easeOut'}, 0);

        this._waysTL.fromTo(this._waysTxt, {alpha: 0}, {alpha: 1, duration: duration}, 0);
    }

    public bounceHit(intencity: number = 0, durr: number = 0.2): gsap.core.Timeline {
        this._bounceTl = Helper.setTimeline(this._bounceTl, new gsap.core.Timeline());
        if (!intencity) return this._bounceTl;

        intencity = Math.min(intencity, 6);
        const inc: number = intencity * 0.05;

        this._bounceTl.to(this._countUp.scale, {duration: durr / 2, x: 1.15 + inc, y: 1.15 + inc}, 0);
        this._bounceTl.to(this._countUp.scale, durr, {x: 1, y: 1, ease: 'bounce.easeOut'}, durr / 2);

        return this._bounceTl;
    }

    public moveDown(scaling: boolean = true, duration: number = 0.5): gsap.core.Timeline {
        this._movementTL = Helper.setTimeline(this._movementTL, new gsap.core.Timeline());

        if (scaling) this._movementTL.to(this.scale, {x: '-=' + 0.27, y: '-=' + 0.27, duration: duration});
        return this._movementTL.to(this.position, {y: '+=' + this.downDistance, duration: duration, ease: 'back.easeIn'});
    }

    public moveToReset(durr: number): gsap.core.Timeline {
        this._movementTL = Helper.setTimeline(this._movementTL, new gsap.core.Timeline());

        this._movementTL.to(this.scale, {x: this.dim.scale.x, y: this.dim.scale.y, duration: durr}, 0);
        return this._movementTL.to(
            this.position,
            durr,
            {y: this.dim.position.y, x: this.dim.position.x, duration: durr, ease: 'power3.easeOut'},
            0
        );
    }

    public stop(
        endValue?: number,
        duration: number = 0.5,
        delay: number = 1,
        useLastAlpha: boolean = false
    ): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline!, new gsap.core.Timeline());
        this._waysTL = Helper.setTimeline(this._waysTL);

        if (this._countUp.alpha == 0) return this._timeline;
        if (endValue) this._countUp.showValue(endValue);

        let startAlpha: number = useLastAlpha ? this._countUp.alpha : 1;
        let startAlphaWays: number = useLastAlpha ? this._waysTxt.alpha : 1;

        this._timeline.fromTo(this._countUp, {alpha: startAlpha}, {delay: delay, duration: duration, alpha: 0}, 0);
        this._timeline.fromTo(this._waysTxt, {alpha: startAlphaWays}, {delay: delay, duration: duration, alpha: 0}, 0);
        this._timeline.play(0.001);
        return this._timeline;
    }

    public playTotalWin(value: number, duration?: number): void {
        this._countUp.text = SlotGame.apiPlugin.currency.formatValue(value);
        this._countUp.scale.set(1, 1);
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.fromTo(this._countUp, 0.2, {alpha: 0}, {alpha: 1});
        tl.to(this, 2, {});
        tl.to(this._countUp, 0.5, {alpha: 0});
        tl.play();
    }

    public onResize(): void {
        this.position = this.dim.position;
        this.scale = this.dim.scale;
    }
}
